import { useState } from "react";
import { ClipboardCopy, Lock, ShieldAlert } from "lucide-react";

import { CredentialTable } from "@/components/console/CredentialTable";
import { PageHeader } from "@/components/console/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { PageTransition } from "@/components/wizard/PageTransition";
import { StepNavigation } from "@/components/wizard/StepNavigation";
import { copyToClipboard } from "@/lib/clipboard";
import { credentialsToEnv, previewToJson } from "@/lib/export";
import { useWizardSession } from "@/hooks/useWizardSession";
import { useWizardComplete } from "@/hooks/wizardQueries";
import { strings } from "@/lib/strings";
import { wizardRoutePath } from "@/lib/steps";
import { toast } from "sonner";

export function SecretsPage() {
  const session = useWizardSession();
  const [revealAll, setRevealAll] = useState(false);
  const [confirmStored, setConfirmStored] = useState(false);
  const complete = useWizardComplete();
  const result = session.applyResult;
  const credentials = result?.provisionedCredentials ?? [];

  async function copyJson() {
    if (!result) return;
    if (await copyToClipboard(previewToJson(result))) {
      toast.success(strings.toast.credentialsCopiedJson);
    } else {
      toast.error(strings.toast.clipboardFailed);
    }
  }

  async function copyEnv() {
    if (!credentials.length) return;
    if (await copyToClipboard(credentialsToEnv(credentials))) {
      toast.success(strings.toast.credentialsCopiedEnv);
    } else {
      toast.error(strings.toast.clipboardFailed);
    }
  }

  async function handleFinish(): Promise<boolean> {
    try {
      const response = await complete.mutateAsync();
      toast.success(strings.toast.finalizeSigningIn);
      window.location.href = response.signInChallengeUrl;
      return false;
    } catch (error) {
      const message = error instanceof Error ? error.message : strings.toast.finalizeFailed;
      toast.error(message);
      return false;
    }
  }

  return (
    <PageTransition routeKey="secrets">
      <PageHeader title={strings.pages.secrets.title} description={strings.pages.secrets.description} />

      <Card className="panel border-primary/20 bg-primary/5">
        <CardContent className="p-4 text-sm text-muted-foreground">
          <p className="font-medium text-foreground">{strings.pages.secrets.storageGuidanceTitle}</p>
          <p className="mt-1">{strings.pages.secrets.storageGuidanceBody}</p>
        </CardContent>
      </Card>

      {!result ? (
        <Card className="panel">
          <CardContent className="flex flex-col items-center gap-3 p-12 text-center text-sm text-muted-foreground">
            <Lock className="size-8" />
            <p>{strings.pages.secrets.noResult}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => setRevealAll((v) => !v)}>
              {revealAll ? strings.pages.secrets.hideAll : strings.pages.secrets.revealAll}
            </Button>
            <Button variant="outline" onClick={copyJson}>
              <ClipboardCopy className="size-4" /> {strings.pages.secrets.copyJson}
            </Button>
            <Button variant="outline" onClick={copyEnv}>
              <ClipboardCopy className="size-4" /> {strings.pages.secrets.copyEnv}
            </Button>
          </div>

          <CredentialTable credentials={credentials} showSecret revealAll={revealAll} />

          <Card className="panel">
            <CardHeader>
              <CardTitle className="text-base">{strings.pages.secrets.finishTitle}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Checkbox
                checked={confirmStored}
                onChange={(event) => setConfirmStored(event.currentTarget.checked)}
                label={strings.pages.secrets.confirmLabel}
                description={strings.pages.secrets.confirmHint}
              />
              <div className="flex gap-3 rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 text-sm">
                <ShieldAlert className="mt-0.5 size-4 shrink-0 text-amber-500" />
                <p className="text-muted-foreground">{strings.pages.secrets.finishBody}</p>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <StepNavigation
        backPath={wizardRoutePath("review")}
        nextLabel={complete.isPending ? strings.pages.secrets.finalizing : strings.pages.secrets.finishButton}
        onNext={handleFinish}
        nextDisabled={!result || !confirmStored || complete.isPending}
        nextDisabledReason={
          !result ? strings.pages.secrets.nextDisabledApply : strings.pages.secrets.nextDisabledConfirm
        }
      />
    </PageTransition>
  );
}
